import { Component } from '@theme/component';

const DEFAULT_INTERVAL = 3500;
const MOBILE_MEDIA_QUERY = '(max-width: 749px)';
const REDUCED_MOTION_MEDIA_QUERY = '(prefers-reduced-motion: reduce)';
const HOVER_MEDIA_QUERY = '(hover: hover)';

/**
 * A custom element that rotates USP bar items vertically on mobile.
 *
 * One item is visible at a time; every few seconds the current item scrolls
 * and fades up while the next scrolls in from below. Rotation pauses on
 * hover, focus, when the bar leaves the viewport, when the page is hidden,
 * and while a block is selected in the theme editor. Users who prefer
 * reduced motion get a static stacked list instead (handled in CSS — this
 * component never starts for them). On desktop (min-width: 750px) the
 * component stays idle and the CSS grid layout applies.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} viewport - The element wrapping the rotating items.
 *
 * @extends Component<Refs>
 */
class UspBarRotator extends Component {
  requiredRefs = ['viewport'];

  /** @type {number} */
  #index = 0;

  /** @type {number | null} */
  #timer = null;

  /** @type {number | null} */
  #leaveTimeout = null;

  #hovered = false;
  #focused = false;
  #inView = true;
  #editorSelected = false;

  /** @type {MediaQueryList} */
  #mobileQuery = window.matchMedia(MOBILE_MEDIA_QUERY);

  /** @type {MediaQueryList} */
  #motionQuery = window.matchMedia(REDUCED_MOTION_MEDIA_QUERY);

  /** @type {MediaQueryList} */
  #hoverQuery = window.matchMedia(HOVER_MEDIA_QUERY);

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLElement[]} */
  #items = [];

  /** @type {HTMLElement | null} */
  #dots = null;

  connectedCallback() {
    super.connectedCallback();

    this.#items = Array.from(this.querySelectorAll('.usp-bar__item'));

    this.#mobileQuery.addEventListener('change', this.#sync);
    this.#motionQuery.addEventListener('change', this.#sync);
    this.addEventListener('pointerenter', this.#handlePointerEnter);
    this.addEventListener('pointerleave', this.#handlePointerLeave);
    this.addEventListener('pointerup', this.#handlePointerEnd);
    this.addEventListener('pointercancel', this.#handlePointerEnd);
    this.addEventListener('focusin', this.#handleFocusIn);
    this.addEventListener('focusout', this.#handleFocusOut);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);
    document.addEventListener('shopify:block:select', this.#handleBlockSelect);
    document.addEventListener('shopify:block:deselect', this.#handleBlockDeselect);

    this.#observer = new IntersectionObserver(this.#handleIntersection);
    this.#observer.observe(this);

    this.#sync();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#mobileQuery.removeEventListener('change', this.#sync);
    this.#motionQuery.removeEventListener('change', this.#sync);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);
    document.removeEventListener('shopify:block:select', this.#handleBlockSelect);
    document.removeEventListener('shopify:block:deselect', this.#handleBlockDeselect);

    this.#observer?.disconnect();
    this.#observer = null;

    this.#clearTimer();
    if (this.#leaveTimeout) clearTimeout(this.#leaveTimeout);
  }

  /**
   * The auto-advance interval in milliseconds.
   * @returns {number} The interval duration.
   */
  get #interval() {
    const parsed = Number(this.dataset.interval);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : DEFAULT_INTERVAL;
  }

  /**
   * Whether the rotator should run.
   * @returns {boolean} True when on mobile, motion is allowed and there is more than one item.
   */
  get #enabled() {
    return this.#mobileQuery.matches && !this.#motionQuery.matches && this.#items.length > 1;
  }

  /**
   * Whether rotation is currently paused by an interaction.
   * @returns {boolean} True when any pause condition holds.
   */
  get #paused() {
    return this.#hovered || this.#focused || !this.#inView || document.hidden || this.#editorSelected;
  }

  /**
   * Starts or stops the rotator to match the current environment.
   * @returns {void}
   */
  #sync = () => {
    if (this.#enabled) {
      this.#start();
    } else {
      this.#stop();
    }
  };

  /**
   * Activates rotation: marks the component ready, shows the current item and starts the timer.
   * @returns {void}
   */
  #start() {
    if (!this.classList.contains('usp-bar__rotator--ready')) {
      this.classList.add('usp-bar__rotator--ready');
      this.#buildDots();
      this.#items[this.#index]?.classList.add('usp-bar__item--active');
      this.#updateDots();
    }

    this.#updateTimer();
  }

  /**
   * Deactivates rotation and restores the CSS-only state.
   * @returns {void}
   */
  #stop() {
    this.#clearTimer();
    this.classList.remove('usp-bar__rotator--ready');
    this.refs.viewport.removeAttribute('aria-live');

    for (const item of this.#items) {
      item.classList.remove('usp-bar__item--active', 'usp-bar__item--leaving');
    }

    this.#index = 0;
  }

  /**
   * Starts or clears the auto-advance timer based on the pause state,
   * and keeps the live region quiet while rotating.
   * @returns {void}
   */
  #updateTimer() {
    this.#clearTimer();

    if (!this.classList.contains('usp-bar__rotator--ready')) return;

    if (this.#paused) {
      this.refs.viewport.setAttribute('aria-live', 'polite');
      return;
    }

    this.refs.viewport.setAttribute('aria-live', 'off');
    this.#timer = window.setInterval(() => this.#show(this.#index + 1), this.#interval);
  }

  #clearTimer() {
    if (this.#timer) {
      clearInterval(this.#timer);
      this.#timer = null;
    }
  }

  /**
   * Shows the item at the given position, animating the current one out.
   * @param {number} next - The target item position (wraps around).
   * @returns {void}
   */
  #show(next) {
    const target = (next + this.#items.length) % this.#items.length;
    if (target === this.#index) return;

    const current = this.#items[this.#index];
    const upcoming = this.#items[target];
    if (!upcoming) return;

    if (this.#leaveTimeout) clearTimeout(this.#leaveTimeout);

    for (const item of this.#items) {
      item.classList.remove('usp-bar__item--leaving');
    }

    current?.classList.remove('usp-bar__item--active');
    current?.classList.add('usp-bar__item--leaving');
    upcoming.classList.add('usp-bar__item--active');

    this.#leaveTimeout = window.setTimeout(() => {
      current?.classList.remove('usp-bar__item--leaving');
      this.#leaveTimeout = null;
    }, 500);

    this.#index = target;
    this.#updateDots();
  }

  /**
   * Builds the pagination dots once. Decorative only, hidden from assistive tech.
   * @returns {void}
   */
  #buildDots() {
    if (this.#dots || this.#items.length < 2) return;

    const dots = document.createElement('div');
    dots.className = 'usp-bar__dots';
    dots.setAttribute('aria-hidden', 'true');

    for (let position = 0; position < this.#items.length; position += 1) {
      const dot = document.createElement('span');
      dot.className = 'usp-bar__dot';
      dots.appendChild(dot);
    }

    this.appendChild(dots);
    this.#dots = dots;
  }

  #updateDots() {
    if (!this.#dots) return;

    Array.from(this.#dots.children).forEach((dot, position) => {
      dot.classList.toggle('usp-bar__dot--active', position === this.#index);
    });
  }

  /**
   * Pauses rotation on hover — but only for pointers that can actually
   * hover. On touch devices a tap or a passing swipe fires pointerenter
   * without a matching pointerleave, which would freeze the rotator.
   * @param {PointerEvent} event - The pointerenter event.
   * @returns {void}
   */
  #handlePointerEnter = (event) => {
    if (event.pointerType === 'touch' || !this.#hoverQuery.matches) return;

    this.#hovered = true;
    this.#updateTimer();
  };

  #handlePointerLeave = () => {
    this.#hovered = false;
    this.#updateTimer();
  };

  /**
   * Defensively clears the hover pause when a touch interaction ends,
   * in case a touch pointer ever set it.
   * @param {PointerEvent} event - The pointerup or pointercancel event.
   * @returns {void}
   */
  #handlePointerEnd = (event) => {
    if (event.pointerType !== 'touch' || !this.#hovered) return;

    this.#hovered = false;
    this.#updateTimer();
  };

  /**
   * Pauses rotation and reveals the item that received focus, so keyboard
   * users always see the element they are on.
   * @param {FocusEvent} event - The focusin event.
   * @returns {void}
   */
  #handleFocusIn = (event) => {
    this.#focused = true;

    const focusedItem =
      event.target instanceof Element ? event.target.closest('.usp-bar__item') : null;
    const position = focusedItem ? this.#items.indexOf(/** @type {HTMLElement} */ (focusedItem)) : -1;

    if (position >= 0) this.#show(position);

    this.#updateTimer();
  };

  #handleFocusOut = () => {
    this.#focused = false;
    this.#updateTimer();
  };

  #handleVisibilityChange = () => {
    this.#updateTimer();
  };

  /**
   * Tracks whether the bar is on screen so it only rotates while visible.
   * @param {IntersectionObserverEntry[]} entries - The observer entries.
   * @returns {void}
   */
  #handleIntersection = (entries) => {
    const entry = entries[entries.length - 1];
    if (!entry) return;

    this.#inView = entry.isIntersecting;
    this.#updateTimer();
  };

  /**
   * Shows the block a merchant selects in the theme editor and pauses rotation.
   * @param {Event} event - The shopify:block:select event.
   * @returns {void}
   */
  #handleBlockSelect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    this.#editorSelected = true;

    const selectedItem = event.target.closest('.usp-bar__item');
    const position = selectedItem ? this.#items.indexOf(/** @type {HTMLElement} */ (selectedItem)) : -1;
    if (position >= 0) this.#show(position);

    this.#updateTimer();
  };

  /**
   * Resumes rotation when the merchant deselects the block.
   * @param {Event} event - The shopify:block:deselect event.
   * @returns {void}
   */
  #handleBlockDeselect = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    this.#editorSelected = false;
    this.#updateTimer();
  };
}

if (!customElements.get('usp-bar-rotator')) {
  customElements.define('usp-bar-rotator', UspBarRotator);
}
